/**
 * ExpandableText Component
 *
 * Renders long text clamped to a fixed number of lines with a "Show more" /
 * "Show less" toggle. The toggle is only rendered when the text actually
 * overflows the clamp (measured from the DOM), so short content stays clean
 * and compact with no dangling button.
 *
 * Designed for use inside clickable cards: the toggle stops click/pointer
 * propagation so expanding text never triggers the parent card's
 * click-to-edit / click-to-open behaviour.
 *
 * Used by TaskCard, KnowledgeCard, and SuggestionCard to keep dense lists
 * readable on mobile.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface ExpandableTextProps {
  /** The full text to display. */
  text: string;
  /** Number of lines to show when collapsed. Defaults to 2. */
  clampLines?: number;
  /** Class names applied to the text element. */
  className?: string;
  /** Class names applied to the toggle button. */
  toggleClassName?: string;
  /** Label for the expand action. Defaults to "Show more". */
  moreLabel?: string;
  /** Label for the collapse action. Defaults to "Show less". */
  lessLabel?: string;
  /** Accessible context appended to the toggle's aria-label (e.g. "task description"). */
  ariaLabelSuffix?: string;
}

export const ExpandableText = React.forwardRef<HTMLParagraphElement, ExpandableTextProps>(
  (
    {
      text,
      clampLines = 2,
      className,
      toggleClassName,
      moreLabel = 'Show more',
      lessLabel = 'Show less',
      ariaLabelSuffix,
    },
    forwardedRef
  ) => {
    const [expanded, setExpanded] = React.useState(false);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const innerRef = React.useRef<HTMLParagraphElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLParagraphElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef]
    );

    // Measure whether the clamped text actually overflows. Re-measures on text
    // changes and on resize so the toggle only appears when needed.
    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;

      const measure = () => {
        // When expanded the element is not clamped, so temporarily compare
        // against a clamped clone-free heuristic: scrollHeight vs the height of
        // `clampLines` lines. Simplest reliable check is done while collapsed.
        if (expanded) return;
        setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
      };

      measure();

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
      }
      return undefined;
    }, [text, clampLines, expanded]);

    if (!text) return null;

    const showToggle = isOverflowing || expanded;

    const stop = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <>
        <p
          ref={setRefs}
          className={className}
          style={
            expanded
              ? undefined
              : {
                  display: '-webkit-box',
                  WebkitLineClamp: clampLines,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }
          }
        >
          {text}
        </p>
        {showToggle && (
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setExpanded((v) => !v);
            }}
            onPointerDown={stop}
            aria-expanded={expanded}
            aria-label={`${expanded ? lessLabel : moreLabel}${ariaLabelSuffix ? ` ${ariaLabelSuffix}` : ''}`}
            className={cn(
              'mt-1 text-xs font-semibold text-primary transition-colors hover:text-primary/80',
              toggleClassName
            )}
          >
            {expanded ? lessLabel : moreLabel}
          </button>
        )}
      </>
    );
  }
);

ExpandableText.displayName = 'ExpandableText';
